import { Link } from "react-router-dom";
import { Instagram, Facebook, Youtube, Mail, MapPin, MessageCircle } from "lucide-react";
import { Logo } from "./Logo";
import { SITE, whatsappLink, ADDRESSES } from "../data/site";
import { COURSES } from "../data/courses";

const EXPLORE = [
  { label: "Home", href: "/#home" },
  { label: "One to One Training", href: "/#one-to-one" },
  { label: "Courses", href: "/#courses" },
  { label: "Career Roadmaps", href: "/#roadmaps" },
  { label: "Our Trainers", href: "/#trainers" },
  { label: "Student Reviews", href: "/#reviews" },
  { label: "FAQ", href: "/#faq" },
];

const POLICY_LINKS = [
  { label: "Privacy Policy", to: "/policies/privacy" },
  { label: "Cancellation & Refund Policy", to: "/policies/refund" },
  { label: "Terms & Payment Policy", to: "/policies/payment" },
];

const TOP_COURSES = ["ccna", "palo-alto", "aws-saa", "azure-104", "ceh-v13", "oscp"];

export const Footer = () => (
  <footer data-testid="site-footer" className="border-t border-sky-500/15 bg-[#04070F]">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
        <div>
          <Logo />
          <p className="mt-5 text-sm text-slate-400 leading-relaxed">
            India's first truly 1-on-1 live IT security academy. Learn from working engineers at Cisco, Palo Alto,
            Microsoft & IBM — with 24x7 labs, recordings and mock interviews at the market's most affordable pricing.
          </p>
          <div className="flex items-center gap-3 mt-6">
            {[
              { icon: Instagram, href: SITE.instagram, id: "footer-instagram" },
              { icon: Facebook, href: SITE.facebook, id: "footer-facebook" },
              { icon: Youtube, href: SITE.youtube, id: "footer-youtube" },
            ].map(({ icon: Icon, href, id }) => (
              <a
                key={id}
                data-testid={id}
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={id}
                className="w-10 h-10 rounded-lg border border-sky-500/25 flex items-center justify-center text-slate-400 hover:text-orange-400 hover:border-orange-500/50 hover:-translate-y-1 transition-all duration-300"
              >
                <Icon className="w-5 h-5" />
              </a>
            ))}
          </div>
        </div>

        <div>
          <h4 className="font-mono2 text-xs uppercase tracking-[0.25em] text-sky-400 font-semibold mb-5">Explore</h4>
          <ul className="space-y-2.5">
            {EXPLORE.map((l) => (
              <li key={l.label}>
                <a href={l.href} className="text-sm text-slate-400 hover:text-white transition-colors duration-200">
                  {l.label}
                </a>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <h4 className="font-mono2 text-xs uppercase tracking-[0.25em] text-sky-400 font-semibold mb-5">Top Courses</h4>
          <ul className="space-y-2.5">
            {TOP_COURSES.map((slug) => {
              const c = COURSES.find((x) => x.slug === slug);
              return (
                <li key={slug}>
                  <Link
                    to={`/courses/${slug}`}
                    className="text-sm text-slate-400 hover:text-white transition-colors duration-200"
                  >
                    {c.title}
                  </Link>
                </li>
              );
            })}
            <li>
              <Link to="/courses" className="text-sm font-semibold text-orange-400 hover:text-orange-300 transition-colors duration-200">
                View all 33 courses →
              </Link>
            </li>
          </ul>
        </div>

        <div>
          <h4 className="font-mono2 text-xs uppercase tracking-[0.25em] text-sky-400 font-semibold mb-5">Policies & Help</h4>
          <ul className="space-y-2.5">
            {POLICY_LINKS.map((p) => (
              <li key={p.to}>
                <Link
                  to={p.to}
                  data-testid={`footer-${p.to.split("/").pop()}-link`}
                  className="text-sm text-slate-400 hover:text-white transition-colors duration-200"
                >
                  {p.label}
                </Link>
              </li>
            ))}
          </ul>
          <div className="mt-6 space-y-3">
            <a href={`mailto:${SITE.email}`} className="flex items-center gap-2.5 text-sm text-slate-400 hover:text-white transition-colors duration-200">
              <Mail className="w-4 h-4 text-orange-500 shrink-0" /> {SITE.email}
            </a>
            <a
              data-testid="footer-whatsapp-btn"
              href={whatsappLink()}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2.5 text-sm text-slate-400 hover:text-white transition-colors duration-200"
            >
              <MessageCircle className="w-4 h-4 text-orange-500 shrink-0" /> {SITE.whatsappDisplay}
            </a>
            <div className="flex items-start gap-2.5 text-xs text-slate-400" data-testid="footer-address-india">
              <MapPin className="w-4 h-4 text-orange-500 shrink-0 mt-0.5" />
              <span><span className="text-slate-200 font-semibold">India:</span> {ADDRESSES.india}</span>
            </div>
            <div className="flex items-start gap-2.5 text-xs text-slate-400" data-testid="footer-address-usa">
              <MapPin className="w-4 h-4 text-orange-500 shrink-0 mt-0.5" />
              <span><span className="text-slate-200 font-semibold">USA:</span> {ADDRESSES.usa}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div className="border-t border-sky-500/10">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 flex flex-col sm:flex-row items-center justify-between gap-3">
        <p className="text-xs text-slate-500">© 2026 LearnWithTAC ({SITE.domain}). All rights reserved.</p>
        <p className="font-mono2 text-[10px] tracking-[0.3em] uppercase text-slate-600">
          Train 1-on-1 <span className="text-orange-500">•</span> Practice 24x7 <span className="text-orange-500">•</span> Get Hired
        </p>
      </div>
    </div>
  </footer>
);
